import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert, Avatar, Box, Button, Card, CardContent, IconButton, InputAdornment, ListItemIcon, Menu, MenuItem,
  Skeleton, Stack, Tab, TablePagination, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import SupportAgentOutlined from "@mui/icons-material/SupportAgentOutlined";
import PersonSearchOutlined from "@mui/icons-material/PersonSearchOutlined";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchDrivers } from "../redux/slices/driverSlice";
import useIsMobile from "../hooks/useIsMobile";
import { gridSx } from "../components/FilterList";
import { DASHBOARD_COLORS as C, STATUS_COLORS, focusRing, statusDot } from "../components/DashboardCard";
import { backButtonStyle, cardStyle, InfoField, pageStyle } from "../components/busDetails/detailLayout";
import { hasNonLatinText } from "../components/misReports/reportExport";

// The shared page style tints every button blue; this page colours its own buttons.
const { "& .MuiButton-root": _pageButtons, ...basePage } = pageStyle;
const page = { ...basePage, bgcolor: C.page, color: C.navy };
const card = { ...cardStyle, height: "auto", borderColor: C.border };
const MIS_PATH = "/schools";
const PAGE_SIZES = [10, 20, 50];

// Station names are often Hindi: list fonts with Devanagari glyphs before the Latin fallbacks.
const DEVANAGARI_FONT = '"Noto Sans Devanagari", "Nirmala UI", "Kohinoor Devanagari", "Mangal", "Segoe UI", Roboto, sans-serif';

const RECORD_TABS = [
  { type: "Driver", noun: "driver", plural: "Drivers", Icon: BadgeOutlined },
  { type: "Conductor", noun: "conductor", plural: "Conductors", Icon: SupportAgentOutlined },
];

const SEARCH_FIELDS = ["contact_person", "mobile1", "dl_no"];
const statusKey = (d) => String(d.police_station_level1_status ?? "").trim().toLowerCase();

const exportButtonSx = {
  height: 38, flexShrink: 0, px: { xs: 1.5, sm: 2 }, borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 14,
  color: "#fff", bgcolor: C.primary, boxShadow: "0 1px 2px rgba(16,42,67,.12)",
  "&:hover": { bgcolor: C.primaryHover, boxShadow: "0 4px 10px rgba(37,99,235,.25)" },
  "&.Mui-disabled": { color: "rgba(255,255,255,.85)", bgcolor: "#93C5FD" },
  ...focusRing,
};

const tabsSx = {
  minHeight: 36, p: 0.5, bgcolor: "#F1F5F9", border: `1px solid ${C.border}`, borderRadius: "12px", maxWidth: "100%", flexShrink: 0,
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTab-root": { minHeight: 34, py: 0, px: 1.5, borderRadius: "9px", textTransform: "none", fontWeight: 600, fontSize: 13.5, color: "#334155", ...focusRing },
  "& .MuiTab-root.Mui-selected": { color: "#fff", bgcolor: C.primary, boxShadow: "0 1px 2px rgba(37,99,235,.35)" },
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 38, borderRadius: "10px", bgcolor: "#fff", fontSize: 14,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: C.primary },
  },
};

const filterButtonSx = (active) => ({
  height: 38, flexShrink: 0, px: 1.5, borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 13.5, whiteSpace: "nowrap",
  color: active ? C.primary : "#334155", bgcolor: active ? C.primarySoft : "#fff", border: `1px solid ${active ? "#BFDBFE" : C.border}`,
  "&:hover": { bgcolor: C.primarySoft, borderColor: "#BFDBFE" },
  ...focusRing,
});

const viewButtonSx = {
  width: { xs: 40, md: 32 }, height: { xs: 40, md: 32 }, borderRadius: "8px", color: C.primary, border: `1px solid ${C.border}`, bgcolor: "#fff",
  "& svg": { fontSize: 18 }, "&:hover": { bgcolor: C.primarySoft, borderColor: "#BFDBFE" },
  ...focusRing,
};

function StatusLabel({ value }) {
  const status = STATUS_COLORS[String(value ?? "").trim().toLowerCase()];
  if (!status) return <Typography component="span" sx={{ fontSize: 13, color: value ? "#334155" : C.muted }}>{value || "—"}</Typography>;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1, py: 0.25, borderRadius: "6px", bgcolor: `${status.color}14`, color: status.color, fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>
      <Box component="span" sx={statusDot(status.color)} />
      {status.label}
    </Box>
  );
}

function StaffCount({ tab, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1, minWidth: 0, borderRadius: "10px", border: `1px solid ${C.border}`, bgcolor: "#F8FAFC" }}>
      <Avatar variant="rounded" sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: C.primarySoft, color: C.primary }}>
        <tab.Icon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: C.muted, lineHeight: 1.3 }}>{tab.plural}</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
      </Box>
    </Box>
  );
}

function EmptyRecords({ noun, filtered, onReset }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ p: 2 }}>
      <PersonSearchOutlined sx={{ color: C.muted }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>
          {filtered ? `No ${noun}s match your search` : `No ${noun} records found`}
        </Typography>
        <Typography sx={{ fontSize: 13, color: C.muted }}>
          {filtered ? "Try a different name, mobile or DL number, or clear the filters." : `No ${noun}s are linked to this police station yet.`}
        </Typography>
      </Box>
      {filtered && <Button size="small" onClick={onReset} sx={{ flexShrink: 0, textTransform: "none", fontWeight: 600, color: C.primary }}>Clear search and filters</Button>}
    </Stack>
  );
}

function RecordCards({ rows, onView }) {
  return (
    <Box component="ul" aria-label="Records" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {rows.map((d, i) => (
        <Box component="li" key={d.driver_id ?? i} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25, borderTop: i ? `1px solid ${C.border}` : "none", minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, color: C.navy, overflowWrap: "anywhere", minWidth: 0 }}>{d.contact_person || "Name not updated"}</Typography>
              {d.police_station_level1_status && <StatusLabel value={d.police_station_level1_status} />}
            </Stack>
            <Typography sx={{ fontSize: 13, color: "#334155", overflowWrap: "anywhere" }}>
              Mobile: {d.mobile1 || "-"} · DL No: {d.dl_no || "-"}
            </Typography>
          </Box>
          {d.driver_id && (
            <IconButton aria-label={`View ${d.contact_person || "record"}`} onClick={() => onView(d)} sx={viewButtonSx}>
              <VisibilityOutlinedIcon />
            </IconButton>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function PoliceStationView() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const ps = location.state?.ps;
  // Coming back from a driver's details restores the tab, search, filter and page the user left.
  const saved = location.state?.stationView ?? {};
  const { list, loading, error, scope } = useSelector((state) => state.drivers);
  const drivers = useMemo(() => (Array.isArray(list) ? list : []), [list]);
  const [tab, setTab] = useState(saved.tab ?? 0);
  const [search, setSearch] = useState(saved.search ?? "");
  const [status, setStatus] = useState(saved.status ?? "");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [paginationModel, setPaginationModel] = useState(saved.paginationModel ?? { page: 0, pageSize: 10 });

  useEffect(() => {
    if (!drivers.length || scope) dispatch(fetchDrivers());
    // Only on open: the MIS page has usually loaded the list already, unless School Details narrowed it to one school.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const stationDrivers = useMemo(
    // eslint-disable-next-line eqeqeq
    () => (ps?.police_station_id != null ? drivers.filter((d) => d.police_station_id != null && String(d.police_station_id) === String(ps.police_station_id)) : []),
    [drivers, ps]
  );
  const lists = useMemo(() => RECORD_TABS.map(({ type }) => stationDrivers.filter((d) => d.firm_types === type)), [stationDrivers]);
  // Only offer statuses that the station's records actually carry.
  const statusOptions = useMemo(
    () => Object.keys(STATUS_COLORS).filter((key) => stationDrivers.some((d) => statusKey(d) === key)),
    [stationDrivers]
  );
  // The whole driver list is loaded (limit 0), so search and filter cover every record, not just one page.
  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    return lists.map((rows) =>
      rows.filter(
        (d) =>
          (!status || statusKey(d) === status) &&
          (!query || SEARCH_FIELDS.some((key) => String(d[key] ?? "").toLowerCase().includes(query)))
      )
    );
  }, [lists, search, status]);

  // MIS Reports kept its tab, search and page on its history entry; without history, open the Police Station tab.
  const goBack = () => (location.key && location.key !== "default" ? navigate(-1) : navigate(MIS_PATH, { state: { misReport: { tab: "Police Station" } } }));
  const back = (
    <Button variant="text" size="small" onClick={goBack} startIcon={<ArrowBackRounded sx={{ fontSize: 18 }} />} sx={{ ...backButtonStyle, textTransform: "none", color: C.primary }}>
      Back to Police Station List
    </Button>
  );

  if (!ps) {
    return (
      <Box sx={page}>
        {back}
        <Alert severity="info">No police station data found. Return to MIS Reports and open a station from the Police Station tab.</Alert>
      </Box>
    );
  }

  const current = RECORD_TABS[tab];
  const activeList = matches[tab];
  const waiting = loading && !drivers.length;
  const filtered = Boolean(search.trim()) || Boolean(status);
  const firstPage = (model) => ({ ...model, page: 0 });

  const changeTab = (_, next) => {
    setTab(next);
    setPaginationModel(firstPage);
  };
  const changeSearch = (value) => {
    setSearch(value);
    setPaginationModel(firstPage);
  };
  const changeStatus = (value) => {
    setStatus(value);
    setFilterAnchor(null);
    setPaginationModel(firstPage);
  };
  const resetFilters = () => {
    setSearch("");
    setStatus("");
    setPaginationModel(firstPage);
  };

  const viewRecord = (d) => {
    navigate(location.pathname, { replace: true, state: { ...location.state, stationView: { tab, search, status, paginationModel } } });
    navigate(`/drivers/${d.driver_id}`);
  };

  // Same jsPDF table as before (Name, Mobile, DL No), limited to the rows the search and filter leave in this tab.
  const exportPDF = () => {
    const doc = new jsPDF();
    // jsPDF's built-in font has no Devanagari glyphs, so a Hindi station name falls back to its registration number.
    const station = hasNonLatinText([], [], [ps.police_station]) ? `Police Station ${ps.register_no || ""}`.trim() : ps.police_station;

    doc.text(
      `${station} - ${tab === 0 ? "Driver" : "Conductor"} List`,
      14,
      15
    );

    autoTable(doc, {
      startY: 20,
      head: [["Name", "Mobile", "DL No"]],
      body: activeList.map((d) => [
        d.contact_person,
        d.mobile1,
        d.dl_no || "-"
      ]),
    });

    doc.save(
      `${ps.police_station}_${tab === 0 ? "drivers" : "conductors"}.pdf`
    );
  };

  const columns = [
    {
      field: "contact_person", headerName: "Name", flex: 1.4, minWidth: 180,
      renderCell: ({ value }) => <Typography noWrap title={value || undefined} sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>{value || "—"}</Typography>,
    },
    { field: "mobile1", headerName: "Mobile", flex: 1, minWidth: 130, valueGetter: (value) => value || "-" },
    { field: "dl_no", headerName: "DL No", flex: 1.1, minWidth: 150, valueGetter: (value) => value || "-" },
    ...(statusOptions.length
      ? [{ field: "police_station_level1_status", headerName: "Verification", flex: 0.9, minWidth: 130, renderCell: ({ value }) => <StatusLabel value={value} /> }]
      : []),
    {
      field: "actions", headerName: "Actions", width: 90, sortable: false, disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.driver_id ? (
          <Tooltip title="View details">
            <IconButton aria-label={`View ${row.contact_person || "record"}`} onClick={() => viewRecord(row)} sx={viewButtonSx}>
              <VisibilityOutlinedIcon />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  let records;
  if (waiting) {
    records = <Box role="status" aria-label="Loading records" sx={{ p: 1.5 }}>{[1, 2, 3].map((i) => <Skeleton key={i} height={36} />)}</Box>;
  } else if (error && !drivers.length) {
    records = (
      <Box sx={{ p: 1.5 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => dispatch(fetchDrivers())}>Retry</Button>}>
          Unable to load driver and conductor records. Check your connection and try again.
        </Alert>
      </Box>
    );
  } else if (!activeList.length) {
    records = <EmptyRecords noun={current.noun} filtered={filtered && lists[tab].length > 0} onReset={resetFilters} />;
  } else if (isMobile) {
    const { pageSize } = paginationModel;
    const pageIndex = Math.min(paginationModel.page, Math.ceil(activeList.length / pageSize) - 1);
    records = (
      <>
        <RecordCards rows={activeList.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)} onView={viewRecord} />
        <TablePagination
          component="div"
          count={activeList.length}
          page={pageIndex}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZES}
          onPageChange={(_, next) => setPaginationModel({ ...paginationModel, page: next })}
          onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: Number(e.target.value) })}
          labelRowsPerPage="Rows"
          sx={{ borderTop: `1px solid ${C.border}`, "& .MuiTablePagination-toolbar": { flexWrap: "wrap", px: 1 } }}
        />
      </>
    );
  } else {
    records = (
      <DataGrid
        rows={activeList}
        getRowId={(row) => row.driver_id ?? `${row.contact_person}-${row.mobile1}`}
        columns={columns}
        rowHeight={46}
        columnHeaderHeight={40}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={PAGE_SIZES}
        disableColumnFilter
        disableRowSelectionOnClick
        aria-label={`${current.plural} of ${ps.police_station}`}
        sx={{ ...gridSx, minHeight: 0 }}
      />
    );
  }

  return (
    <Box sx={page}>
      {back}

      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "flex-start" }} justifyContent="space-between" spacing={1.5} mb={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" fontWeight={700} sx={{ fontFamily: DEVANAGARI_FONT, fontSize: { xs: 20, sm: 23 }, lineHeight: 1.35, color: C.navy, overflowWrap: "anywhere" }}>
            {ps.police_station || "Police station name not updated"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.muted, overflowWrap: "anywhere" }}>
            Registration No: {ps.register_no || "Not updated"}
          </Typography>
        </Box>
        <Button onClick={exportPDF} disabled={!activeList.length} startIcon={<PictureAsPdfRounded />} aria-label={`Export ${current.plural} PDF`} sx={{ ...exportButtonSx, alignSelf: { xs: "flex-start", sm: "auto" } }}>
          Export PDF
        </Button>
      </Stack>

      <Card elevation={0} sx={card}>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "minmax(0,1.2fr) repeat(2, minmax(0,1fr))" }, gap: 1.25, alignItems: "center" }}>
            <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
              <InfoField label="Total Records (MIS)" value={ps.total} />
            </Box>
            {RECORD_TABS.map((t, i) => <StaffCount key={t.type} tab={t} value={waiting ? "…" : lists[i].length} />)}
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ ...card, mt: 1.5, overflow: "hidden" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.25, p: { xs: 1.25, sm: 1.5 }, borderBottom: `1px solid ${C.border}` }}>
          <Tabs value={tab} onChange={changeTab} variant="scrollable" scrollButtons={false} aria-label="Record type" sx={tabsSx}>
            {RECORD_TABS.map(({ plural }, i) => <Tab key={plural} label={`${plural} (${waiting ? "…" : matches[i].length})`} />)}
          </Tabs>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: { xs: "1 1 100%", md: "0 1 440px" } }}>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(e) => changeSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && changeSearch("")}
              placeholder="Search by name, mobile or DL number"
              sx={inputSx}
              slotProps={{
                htmlInput: { "aria-label": "Search drivers and conductors" },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: C.muted }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => changeSearch("")} edge="end">
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            {statusOptions.length > 0 && (
              <>
                <Button
                  onClick={(e) => setFilterAnchor(e.currentTarget)}
                  startIcon={<TuneRoundedIcon />}
                  aria-haspopup="menu"
                  aria-label={status ? `Filter, ${STATUS_COLORS[status].label} applied` : "Filter"}
                  sx={filterButtonSx(Boolean(status))}
                >
                  {status ? STATUS_COLORS[status].label : "Filter"}
                </Button>
                <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} slotProps={{ paper: { sx: { mt: 0.5, borderRadius: "10px", minWidth: 200 } } }}>
                  <Typography sx={{ px: 2, pt: 0.5, pb: 0.75, fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".04em" }}>Verification status</Typography>
                  {["", ...statusOptions].map((key) => (
                    <MenuItem key={key || "all"} selected={status === key} onClick={() => changeStatus(key)} sx={{ fontSize: 14, gap: 1 }}>
                      <ListItemIcon sx={{ minWidth: "20px !important" }}>{status === key && <CheckRounded sx={{ fontSize: 18, color: C.primary }} />}</ListItemIcon>
                      {key ? <><Box component="span" sx={statusDot(STATUS_COLORS[key].color)} />{STATUS_COLORS[key].label}</> : "All statuses"}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Box>
        {records}
      </Card>
    </Box>
  );
}
